import React, { Suspense, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { showSwal } from "../Helper/ShowAlert.js";
import { TRANSPORTERS } from "../Helper/partnerLabels.js";
import RouteFallback from "../components/shared/RouteFallback";
import FkAmbientCss from "../motionKit/FkAmbientCss";
import PanelBoundary from "../components/shared/PanelBoundary";
import { pageTransition } from "../components/motion/presets";
import { logout } from "../redux/action";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import Header from "../Pages/Admin/Header.jsx";
import SideBar from "../Pages/Admin/SideBar.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faFileInvoice,
  faReply,
  faLayerGroup,
  faPlaneDeparture,
  faUsers,
  faUserShield,
  faUserGear,
  faMoneyBillTransfer,
  faUser,
  faRightFromBracket,
  faTruck,
  faFileAlt,
  faEnvelope,
  faPenToSquare,
  faBoxesStacked,
} from "@fortawesome/free-solid-svg-icons";
import { useFeatureFlags } from "../context/FeatureFlagContext";
import { useSideBarContext } from "../Providers/SideBarProvider";
import CommandPalette from "../components/CommandPalette.jsx";
import AIChatBot from "../components/shared/AIChatBot.jsx";

function isWorkspaceViewportPath(pathname) {
  return (
    pathname.includes("/incomplete-queries") ||
    pathname.includes("/all-quotes") ||
    pathname.includes("/newQuotePage/")
  );
}

// Read the JWT that redux persisted to localStorage['state'] (see redux/store.js).
// Used to distinguish a genuinely-gone session from a transient falsy redux read.
function readPersistedToken() {
  try {
    const raw = localStorage.getItem("state");
    if (!raw) return null;
    return JSON.parse(raw)?.reducer?.userToken || null;
  } catch {
    return null;
  }
}

// True only when the token is decodable AND its exp is in the past. A token we
// can't decode is treated as "not a valid session" (expired) so we still log out.
function isTokenExpired(token) {
  try {
    const { exp } = jwtDecode(token);
    if (!exp) return false; // no expiry claim → treat as still valid
    return Date.now() / 1000 > exp;
  } catch {
    return true;
  }
}

const AuthRoot = () => {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const loginDetail = useSelector((state) => state.reducer.userLoggedIn);
  const { gcpEnabled } = useFeatureFlags();
  const { setActivePage } = useSideBarContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const workspaceViewportMode = isWorkspaceViewportPath(pathname);
  let sideBarItems = {};
  const logouts = {
    name: "Logout",
    path: "/loginPage",
    icon: ({ color }) => <FontAwesomeIcon icon={faRightFromBracket} style={{ color: color || '#EF4444' }} />
  };
  const setting = {
    name: "My Profile",
    path: `/${window.location.pathname.split("/")[1]}/profile`,
    icon: ({ color }) => <FontAwesomeIcon icon={faUser} style={{ color: color || '#3B82F6' }} />,
  };

  const additionToSideBar = () => {
    if (loginDetail) {
      switch (loginDetail.role) {
        case "admin":
          sideBarItems.dashboard = {
            name: "Dashboard",
            path: "/admin/dashboard",
            icon: ({ color }) => <FontAwesomeIcon icon={faChartLine} style={{ color: color || '#6366F1' }} />,
          };
          sideBarItems.Quote = {
            name: "Quotes",
            path: `/admin/all-quotes`,
            icon: ({ color }) => <FontAwesomeIcon icon={faFileInvoice} style={{ color: color || '#10B981' }} />,
          }
          sideBarItems.airlines = {
            name: "Airlines",
            path: `/admin/airlines`,
            icon: ({ color }) => <FontAwesomeIcon icon={faPlaneDeparture} style={{ color: color || '#0EA5E9' }} />,
          }
          sideBarItems.manualQueries = {
            name: "Manual Queries",
            path: "/admin/manual-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faPenToSquare} style={{ color: color || '#8B5CF6' }} />,
          };
          sideBarItems.queries = {
            name: "Email Queries",
            path: "/admin/incomplete-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faEnvelope} style={{ color: color || '#F59E0B' }} />,
          };
          sideBarItems.template = {
            name: "Templates",
            path: `/admin/templates`,
            icon: ({ color }) => <FontAwesomeIcon icon={faLayerGroup} style={{ color: color || '#A855F7' }} />,
          }
                    sideBarItems.agents = {
            name: TRANSPORTERS,
            path: "/admin/agents",
            icon: ({ color }) => <FontAwesomeIcon icon={faUsers} style={{ color: color || '#14B8A6' }} />,
          };
          sideBarItems.awbStock = {
            name: "AWB Stock",
            path: `/admin/awb-stock`,
            icon: ({ color }) => <FontAwesomeIcon icon={faBoxesStacked} style={{ color: color || '#0EA5E9' }} />,
          };
          sideBarItems.operators = {
            name: "Operators",
            path: "/admin/operators",
            icon: ({ color }) => <FontAwesomeIcon icon={faUserGear} style={{ color: color || '#4F46E5' }} />,
          };
          break;
        case "superAdmin":
          sideBarItems.dashboard = {
            name: "Dashboard",
            path: "/admin/dashboard",
            icon: ({ color }) => <FontAwesomeIcon icon={faChartLine} style={{ color: color || '#6366F1' }} />,
          };
          sideBarItems.manualQueries = {
            name: "Manual Queries",
            path: "/admin/manual-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faPenToSquare} style={{ color: color || '#8B5CF6' }} />,
          };
          sideBarItems.queries = {
            name: "Email Queries",
            path: "/admin/incomplete-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faEnvelope} style={{ color: color || '#F59E0B' }} />,
          };
          sideBarItems.Quote = {
            name: "Quotes",
            path: `/admin/all-quotes`,
            icon: ({ color }) => <FontAwesomeIcon icon={faFileInvoice} style={{ color: color || '#10B981' }} />,
          }
          if (gcpEnabled) {
            sideBarItems.quotesAirlineResponse = {
              name: "Responses",
              path: `/admin/quotes-airline-response`,
              icon: ({ color }) => <FontAwesomeIcon icon={faReply} style={{ color: color || '#06B6D4' }} />,
            }
          }
          sideBarItems.template = {
            name: "Templates",
            path: `/admin/templates`,
            icon: ({ color }) => <FontAwesomeIcon icon={faLayerGroup} style={{ color: color || '#A855F7' }} />,
          }
          sideBarItems.airlines = {
            name: "Airlines",
            path: `/admin/airlines`,
            icon: ({ color }) => <FontAwesomeIcon icon={faPlaneDeparture} style={{ color: color || '#0EA5E9' }} />,
          }
          sideBarItems.agents = {
            name: TRANSPORTERS,
            path: "/admin/agents",
            icon: ({ color }) => <FontAwesomeIcon icon={faUsers} style={{ color: color || '#14B8A6' }} />,
          };
          sideBarItems.awbStock = {
            name: "AWB Stock",
            path: `/admin/awb-stock`,
            icon: ({ color }) => <FontAwesomeIcon icon={faBoxesStacked} style={{ color: color || '#0EA5E9' }} />,
          };
          sideBarItems.admin = {
            name: "Admins",
            path: "/admin/admins",
            icon: ({ color }) => <FontAwesomeIcon icon={faUserShield} style={{ color: color || '#DC2626' }} />,
          };
          sideBarItems.operators = {
            name: "Operators",
            path: "/admin/operators",
            icon: ({ color }) => <FontAwesomeIcon icon={faUserGear} style={{ color: color || '#4F46E5' }} />,
          };
          sideBarItems.transactions = {
            name: "Transactions",
            path: "/admin/transactions",
            icon: ({ color }) => <FontAwesomeIcon icon={faMoneyBillTransfer} style={{ color: color || '#22C55E' }} />,
          };
          break;
        case "operator":
          sideBarItems.dashboard = {
            name: "Dashboard",
            path: "/operator/dashboard",
            icon: ({ color }) => <FontAwesomeIcon icon={faChartLine} style={{ color: color || '#6366F1' }} />,
          };
          sideBarItems.manualQueries = {
            name: "Manual Queries",
            path: "/operator/manual-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faPenToSquare} style={{ color: color || '#8B5CF6' }} />,
          };
          sideBarItems.queries = {
            name: "Email Queries",
            path: "/operator/incomplete-queries",
            icon: ({ color }) => <FontAwesomeIcon icon={faEnvelope} style={{ color: color || '#F59E0B' }} />,
          };
          sideBarItems.Quote = {
            name: "Quotes",
            path: `/operator/all-quotes`,
            icon: ({ color }) => <FontAwesomeIcon icon={faFileInvoice} style={{ color: color || '#10B981' }} />,
          }
          if (gcpEnabled) {
            sideBarItems.quotesAirlineResponse = {
              name: "Responses",
              path: `/operator/quotes-airline-response`,
              icon: ({ color }) => <FontAwesomeIcon icon={faReply} style={{ color: color || '#06B6D4' }} />,
            }
          }
          sideBarItems.airlines = {
            name: "Airlines",
            path: `/operator/airlines`,
            icon: ({ color }) => <FontAwesomeIcon icon={faPlaneDeparture} style={{ color: color || '#0EA5E9' }} />,
          }
          sideBarItems.awbStock = {
            name: "AWB Stock",
            path: `/operator/awb-stock`,
            icon: ({ color }) => <FontAwesomeIcon icon={faBoxesStacked} style={{ color: color || '#0EA5E9' }} />,
          }
          sideBarItems.agents = {
            name: TRANSPORTERS,
            path: "/operator/agents",
            icon: ({ color }) => <FontAwesomeIcon icon={faUsers} style={{ color: color || '#14B8A6' }} />,
          };
          break;
        case "user":
          sideBarItems.content = {
            name: "Create Freight",
            path: `/user/freight-form`,
            icon: ({ color }) => <FontAwesomeIcon icon={faTruck} style={{ color: color || '#F97316' }} />,
          };
          sideBarItems.mycontent = {
            name: "My Quotes",
            path: `/user/my-quotes`,
            icon: ({ color }) => <FontAwesomeIcon icon={faFileAlt} style={{ color: color || '#3B82F6' }} />,
          };
          sideBarItems.transactions = {
            name: "Transactions",
            path: `/user/transactions`,
            icon: ({ color }) => <FontAwesomeIcon icon={faMoneyBillTransfer} style={{ color: color || '#22C55E' }} />,
          };
          break;
      }
      sideBarItems = { ...sideBarItems, setting, logouts };
    }

    // Organize items into groups for superAdmin and admin roles
    if (loginDetail && (loginDetail.role === "superAdmin" || loginDetail.role === "admin")) {
      const organizedItems = {
        groups: [],
        standalone: []
      };

      // Group 1: Quotes & Queries
      const quotesQueriesGroup = {
        title: "Quotes & Queries",
        items: []
      };
      if (sideBarItems.manualQueries) quotesQueriesGroup.items.push(sideBarItems.manualQueries);
      if (sideBarItems.queries) quotesQueriesGroup.items.push(sideBarItems.queries);
      if (sideBarItems.Quote) quotesQueriesGroup.items.push(sideBarItems.Quote);
      if (sideBarItems.quotesAirlineResponse) quotesQueriesGroup.items.push(sideBarItems.quotesAirlineResponse);
      if (quotesQueriesGroup.items.length > 0) {
        organizedItems.groups.push(quotesQueriesGroup);
      }

      // Group 2: Management
      const managementGroup = {
        title: "Management",
        items: []
      };
      if (sideBarItems.template) managementGroup.items.push(sideBarItems.template);
      if (sideBarItems.airlines) managementGroup.items.push(sideBarItems.airlines);
      // AWB stock had no admin entry at all — the only way in was the MAWB card
      // or an airline's detail page, so a whole stock ledger was invisible.
      if (sideBarItems.awbStock) managementGroup.items.push(sideBarItems.awbStock);
      if (sideBarItems.agents) managementGroup.items.push(sideBarItems.agents);
      if (sideBarItems.operators) managementGroup.items.push(sideBarItems.operators);
      if (sideBarItems.admin) managementGroup.items.push(sideBarItems.admin);
      if (sideBarItems.users) managementGroup.items.push(sideBarItems.users);
      if (managementGroup.items.length > 0) {
        organizedItems.groups.push(managementGroup);
      }

      // Standalone items
      if (sideBarItems.dashboard) organizedItems.standalone.push(sideBarItems.dashboard);
      if (sideBarItems.transactions) organizedItems.standalone.push(sideBarItems.transactions);
      if (sideBarItems.setting) organizedItems.standalone.push(sideBarItems.setting);
      if (sideBarItems.logouts) organizedItems.standalone.push(sideBarItems.logouts);

      return organizedItems;
    }

    // For other roles, return flat structure
    return { groups: [], standalone: Object.values(sideBarItems) };
  };
  useEffect(() => {
    let tokenExpirationTimer = null;

    if (loginToken) {
      try {
        const decodedToken = jwtDecode(loginToken);
        const expirationTime = decodedToken.exp;
        const currentTime = Date.now() / 1000;

        if (expirationTime && currentTime > expirationTime) {
          // Token already expired
          dispatch(logout());
          showSwal("Token Expired", "Logged Out", 400, () => navigate("/loginPage"));
        } else if (expirationTime) {
          // Token is still valid, set up automatic logout when it expires
          const timeUntilExpiration = (expirationTime - currentTime) * 1000; // Convert to milliseconds

          tokenExpirationTimer = setTimeout(() => {
            dispatch(logout());
            showSwal("Session Expired", "You have been logged out", 400, () => navigate("/loginPage"));
          }, timeUntilExpiration);
        }
      } catch (error) {
        // Invalid token
        console.error("Error decoding token:", error);
        dispatch(logout());
        showSwal("Invalid Token", "Please login again", 400, () => navigate("/loginPage"));
      }
      // if (
      //   window.location.pathname.split("/")[1] !== loginDetail.role ||
      //   window.location.pathname.split("/")[1] === "superAdmin"
      // ) {
      //   let roleToNavigate =
      //     loginDetail.role === "admin" || loginDetail.role === "superAdmin"
      //       ? "admin"
      //       : "user";
      //   navigate(`/${roleToNavigate}/${loginDetail.role === "admin" || loginDetail.role === "superAdmin" ? "users" : "content"}`);
      // }
      // if (window.location.pathname.split("/")[2] === undefined) {
      //   navigate(`/${window.location.pathname.split("/")[1]}/${loginDetail.role === "admin" || loginDetail.role === "superAdmin" ? "dashboard" : "content"}`);
      // }
    } else {
      // Intentional logout from Header/SideBar sets this before clearing the token,
      // so we must not show a second toast here (otherwise users see success + error).
      if (localStorage.getItem("myProcess") === "logout") {
        localStorage.removeItem("myProcess");
        return;
      }

      // Guard against a SPURIOUS logout when redux `userToken` momentarily reads
      // falsy even though a valid session is still persisted. The store persists
      // the whole state to localStorage['state'] on every change (see
      // redux/store.js), and a genuine expiry/logout ALWAYS clears that persisted
      // token too (dispatch(logout()) → saveState). So if localStorage still holds
      // a non-expired token here, redux and localStorage have simply diverged for
      // an instant (e.g. a transient render on a heavy lazy route like the freight
      // calculator) and we must NOT tear down the session — the next render with
      // the rehydrated token recovers cleanly. Only log out when the PERSISTED
      // session is genuinely gone or expired.
      const persistedToken = readPersistedToken();
      if (persistedToken && !isTokenExpired(persistedToken)) {
        // Transient divergence — keep the user signed in.
        return;
      }

      dispatch(logout());
      showSwal("Token Expired", "Logged Out", 400, () => navigate("/loginPage"));
    }

    // Cleanup function to clear the timer when component unmounts or token changes
    return () => {
      if (tokenExpirationTimer) {
        clearTimeout(tokenExpirationTimer);
      }
    };
  }, [loginToken, dispatch, navigate]);

  let isAdmin = false; // if path contains /admin then admin
  if (window.location.pathname.split("/")[1] === "admin") {
    isAdmin = true;
  }

  // Build the sidebar once, and a flat nav list reused by the command palette
  // and the route→title sync below.
  const sidebar = additionToSideBar();
  // The copilot's floating mic is a fixed 56px circle pinned 24px off the right
  // edge, vertically centred, above every layer — it sat on top of the shipment
  // inspector's fields and hid the paste-parser's "Apply to form" button. Where
  // it is mounted we reserve the gutter it occupies instead of letting it cover
  // whatever the operator was trying to click.
  const copilotMounted = ["admin", "superAdmin", "operator"].includes(loginDetail?.role);
  const navFlat = [
    ...(sidebar.groups || []).flatMap((g) => g.items || []),
    ...(sidebar.standalone || []),
  ];

  // Keep the header title in sync with the current route — fixes the stale
  // "Dashboard" title on hard reload / deep-link (activePage was previously only
  // set when a sidebar item was clicked).
  useEffect(() => {
    const match = navFlat
      .filter((it) => it.path && pathname.startsWith(it.path))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (match?.name) {
      setActivePage(match.name);
    } else {
      // Detail routes don't prefix-match any sidebar path (e.g.
      // /operator/newQuotePage/:id), so the title would go stale. Map known
      // detail-route fragments to a title; role-agnostic since paths start
      // with /admin or /operator. "/manual-query/" (singular, trailing slash)
      // is deliberately more specific than the "/manual-queries" list route,
      // which already matches nav above.
      const detailTitlePatterns = [
        ["/newQuotePage/", "Quote Workspace"],
        ["/generate-documents", "Document Studio"],
        ["/manual-query/", "Manual Query"],
        ["/complete-quote", "Quote Workspace"],
      ];
      const fallback = detailTitlePatterns.find(([fragment]) =>
        pathname.includes(fragment)
      )?.[1];
      if (fallback) setActivePage(fallback);
    }
  }, [pathname]);

  return (
    <div
      className={`flex w-full bg-fk-canvas dark:bg-[oklch(0.168_0.021_264)] ${
        workspaceViewportMode
          ? "h-dvh max-h-dvh min-h-0 overflow-hidden"
          : "min-h-screen"
      }`}
    >
      {/* App-wide ambient: lightweight CSS blur-blobs (Lanura technique),
          fixed BEHIND content at z-0 so the soft blue/sky/indigo glow shows
          through the transparent canvas gaps between cards — pure CSS, ~zero
          cost, and blur-3xl reads softly on white where the old canvas aurora
          smudged. Content wrapper below is lifted to z-10; sidebar is z-30.
          Reduced-motion freezes the blobs to a static tint. */}
      <FkAmbientCss className="fixed z-0" intensity={0.8} calm />
      <SideBar sideBarItems={sidebar} />
      <div
        className={`relative z-10 flex min-w-0 flex-1 flex-col ${
          workspaceViewportMode ? "min-h-0 h-dvh max-h-dvh overflow-hidden" : "min-h-screen"
        }`}
      >
        <Header />
        {/* No copilot gutter any more. Every authed page used to give up 80px
            of its right edge for the lifetime of the session, because the mic
            floated at the vertical middle of that edge and would otherwise land
            on top of content. Now that it is docked in the bottom-right corner
            it hovers over the one spot nothing is laid out in — the same way the
            Notes button always has — so the lane it was holding open across the
            whole app is just margin. */}
        <main
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden ${
            workspaceViewportMode ? "overflow-y-hidden" : "overflow-y-auto"
          }`}
        >
          {/* Suspense MUST wrap AnimatePresence, not sit inside it: when a
              lazy route suspended INSIDE AnimatePresence mode="wait", the
              entering motion.div never ran its enter animation and stayed at
              its initial opacity:0 — the page looked blank (only the ambient
              behind it showed) until a reload cached the chunk. With Suspense
              outside, the fade only ever runs on already-resolved content. */}
          <Suspense fallback={<RouteFallback />}>
            <AnimatePresence mode="wait">
              <motion.div key={pathname} {...pageTransition} className="h-full">
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </Suspense>
        </main>
      </div>
      <CommandPalette items={navFlat.filter((it) => it.name !== "Logout")} />
      {/* Copilot is app-wide for admins/operators (it derives quote context
          from the URL) so its navigate/highlight actions work from any page. */}
      {/* A copilot crash must never take the app down with it. */}
      {copilotMounted && (
        <PanelBoundary label="Copilot" silent>
          <AIChatBot />
        </PanelBoundary>
      )}
    </div>
  );
};

export default AuthRoot;
